import React, { useState, useRef, useEffect } from "react";
import { View, TextInput, Animated } from "react-native";
import i18n from "../locales/i18n";

interface FloatingLabelInputProps {
  label: string | string[];
  value: string;
  keyboardType?: "default" | "numeric" | "email-address" | "phone-pad";
  onChangeText?: (text: string) => void;
  locale?: string;
  [key: string]: any;
}

const FloatingLabelInput = ({
  label = "",
  value = "",
  keyboardType = "default",
  onChangeText = (text: string) => {}, // Default to a no-op function
  locale,
  ...props
}: FloatingLabelInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const animatedValue = useRef(new Animated.Value(0)).current;
  const currentLocale = locale || i18n.locale;
  // Update animation when value changes from outside
  useEffect(() => {
    if (value && value.length > 0) {
      animateLabel(1);
    } else if (!isFocused) {
      animateLabel(0);
    }
  }, [value]);

  const handleFocus = () => {
    setIsFocused(true);
    animateLabel(1);
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (!value || value.length === 0) {
      animateLabel(0);
    }
  };

  const animateLabel = (toValue: number) => {
    Animated.timing(animatedValue, {
      toValue,
      duration: 200,
      useNativeDriver: false,
    }).start();
  };

  const labelStyle = {
    left: currentLocale === "ar" ? 0 : 20,
    right: currentLocale === "ar" ? 20 : 0,
    alignSelf: "flex-start" as const,
    width: "auto" as const,
    top: animatedValue.interpolate({
      inputRange: [0, 1],
      outputRange: [18, -8],
    }),
    fontSize: animatedValue.interpolate({
      inputRange: [0, 1],
      outputRange: [18, 16],
    }),
    color: animatedValue.interpolate({
      inputRange: [0, 1],
      outputRange: ["#999", "rgba(5, 150, 208, 1)"],
    }),
    backgroundColor: "white",
    paddingHorizontal: 4,
    borderRadius: 10,
    zIndex: 1,
  };

  return (
    <View style={{ marginVertical: 10, width: "85%", alignSelf: "center" }}>
      <View style={{ position: "relative" }}>
        <View style={{ position: "absolute", zIndex: 1 }}>
          <Animated.Text style={labelStyle} className="font-bold">
            {label}
          </Animated.Text>
        </View>

        <TextInput
          {...props}
          value={value}
          autoCapitalize="none"
          onChangeText={(text) => {
            onChangeText(text);
            if (text && text.length > 0) {
              animateLabel(1);
            } else if (!isFocused) {
              animateLabel(0);
            }
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={[
            {
              backgroundColor: "white",
              borderWidth: isFocused ? 2 : 1,
              borderColor: isFocused ? "rgba(5, 150, 208, 1)" : "#ddd",
              borderRadius: 15,
              padding: 15,
              fontSize: 16,
              textAlign: currentLocale === "ar" ? "right" : "left",
              fontWeight: "600",
              color: "black",
            },
            props.style,
          ]}
          placeholder=""
          keyboardType={keyboardType}
        />
      </View>
    </View>
  );
};

export default FloatingLabelInput;
